import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemContent from "@mui/joy/ListItemContent";
import Typography from "@mui/joy/Typography";
import { memo, useEffect } from "react";
import { Box } from "@mui/joy";
import { PatientType } from "api/patientApi";
import { patientAtom } from "recoil/patientAtom";
import { useRecoilState, useSetRecoilState } from "recoil";
import { usePrevious } from "@uidotdev/usehooks";
import { layoutAtom } from "recoil/layoutAtom";
import PatientItem from "./PatientItem";
import {
  alwaysOpenPatientListMenyKey,
  menuSelector,
  patientListMenuKey,
} from "recoil/menuAtom";
import RemindMessageSender from "./remind/RemindMessageSender";
import { SidebarMenuType } from "components/sidebar/SidebarForPatient";
import { theme } from "../../styles/theme";
import { ArrowUpIconSvg } from "static/image/common/SvgAssets";

function PatientSubList({
  menuKey,
  title,
  patientList,
  selected,
  setSelected,
  setCurrentMenu,
}: {
  menuKey: patientListMenuKey;
  title: string;
  patientList: PatientType[];
  selected?: string;
  setSelected: React.Dispatch<React.SetStateAction<string | undefined>>;
  setCurrentMenu?: React.Dispatch<React.SetStateAction<SidebarMenuType>>;
}) {
  const [patientState, setPatientState] = useRecoilState(patientAtom);
  const previousPatientState = usePrevious(patientState);
  const setLayoutState = useSetRecoilState(layoutAtom);

  const [isOpen, setIsOpen] = useRecoilState(menuSelector(menuKey));

  useEffect(() => {
    if (alwaysOpenPatientListMenyKey.includes(menuKey)) {
      setIsOpen(true);
    }
  }, []);

  useEffect(() => {
    if (patientState?.patientId !== previousPatientState?.patientId) {
      setLayoutState((state) => ({ ...state, isRightSideBarOpen: true }));
    }
  }, [patientState]);

  return (
    <ListItem
      nested
      sx={{
        "--ListItem-minHeight": "auto",
        "--ListItem-hoverBg": "transparent",
        "--ListItem-selectedBg": "transparent",
        "&:hover": { backgroundColor: "transparent" },
        "&.Mui-selected": { backgroundColor: "transparent" },
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={() => {
          setIsOpen((isOpen) => !isOpen);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsOpen((isOpen) => !isOpen);
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          py: 0,
          borderWidth: 0,
          minHeight: "auto",
          "&:hover": { backgroundColor: "transparent" },
          "&:active": { backgroundColor: "transparent" },
          "&:focus-visible": { outline: "none" },
        }}
      >
        <ListItemContent>
          <Typography
            variant={"Footnote"}
            sx={{ color: theme.vars.palette.Content.Strong, fontWeight: 700 }}
          >
            {title}
            <Typography
              variant={"Footnote"}
              sx={{
                color: theme.vars.palette.Content.Weak,
                fontWeight: 700,
                pl: 0.5,
              }}
            >
              {patientList.length}
            </Typography>
          </Typography>
        </ListItemContent>
        <Box
          sx={{
            ...(!isOpen && {
              transform: "rotate(180deg)",
              transformOrigin: "center",
            }),
            transition: "transform 0.2s",
          }}
        >
          <ArrowUpIconSvg color={theme.vars.palette.Content.Highlight} />
        </Box>
      </Box>

      {isOpen && (
        <List sx={{ mt: "8px", gap: "4px" }}>
          {menuKey === "patientList_isRemindRequired" && (
            <RemindMessageSender patientList={patientList} />
          )}
          {patientList?.map((patient, index) => (
            <ListItem
              key={`${title}_${patient.patientId}`}
              sx={{ "--ListItem-minHeight": "auto", py: 0, mb: "4px" }}
            >
              <PatientItem
                title={title}
                patient={patient}
                selected={selected}
                setSelected={setSelected}
                setCurrentMenu={setCurrentMenu}
                menuKey={menuKey}
              />
            </ListItem>
          ))}
        </List>
      )}
    </ListItem>
  );
}

export default memo(PatientSubList);
